/**
 * Admin AI Free Usage Page
 *
 * Platform admins: configure platform models, AI tiers (with caps and models), user tier assignment, and free usage limits (time window, session message limit).
 * Tenant admins: view limits; configure default model in AI Settings.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import ResearchSearchSettingsCard from '@/components/ai/ResearchSearchSettingsCard';

interface LimitsConfig {
  id: string;
  tenantId: string | null;
  sessionMessageLimit: number | null;
  tokenLimitPerDevice: number;
  timeWindowHours: number;
  freeTierTokensPerUser: number;
  paidTierTokensPerUser: number | null;
  dailyTokensPerUser: number | null;
  updatedAt: string;
}

interface DefaultModel {
  id: string;
  displayName: string;
  modelName: string;
}

interface PlatformModel {
  id: string;
  modelName: string;
  displayName: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  configuration?: Record<string, string>;
}

interface AdminLimitsResponse {
  limits: LimitsConfig | null;
  defaultModel: DefaultModel | null;
  note: string;
  isPlatformAdmin?: boolean;
  platformModels?: PlatformModel[];
  platformDefaults?: { free_tier: DefaultModel | null; paid: DefaultModel | null };
}

interface AiTier {
  id: string;
  name: string;
  dailyTokenCap: number;
  defaultModelConfigurationId: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  modelIds?: string[];
}

interface UserWithTier {
  userId: string;
  email: string;
  tierId: string | null;
  tierName: string | null;
}

const AdminAIFreeUsagePage: React.FC = () => {
  const [data, setData] = useState<AdminLimitsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    sessionMessageLimit: 50,
    tokenLimitPerDevice: 100000,
    timeWindowHours: 24,
    freeTierTokensPerUser: 500000,
    paidTierTokensPerUser: null as number | null,
    dailyTokensPerUser: null as number | null,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configuringModel, setConfiguringModel] = useState<PlatformModel | null>(null);
  const [addForm, setAddForm] = useState({ modelName: '', displayName: '', description: '', endpoint: '', model: '', apiKey: '' });
  const [configForm, setConfigForm] = useState({ modelName: '', displayName: '', description: '', apiKey: '', endpoint: '', currentModel: '' });
  const [aiTiers, setAiTiers] = useState<AiTier[]>([]);
  const [usersWithTiers, setUsersWithTiers] = useState<UserWithTier[]>([]);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState<AiTier | null>(null);
  const [tierForm, setTierForm] = useState({
    name: '',
    dailyTokenCap: 500000,
    defaultModelConfigurationId: null as string | null,
    modelIds: [] as string[],
  });

  const load = async (cacheBust = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = cacheBust ? `/ai/admin/limits?_t=${Date.now()}` : '/ai/admin/limits';
      const res = await api.get<AdminLimitsResponse>(url);
      const json = res.data;
      setData(json);
      if (json.limits) {
        setForm({
          sessionMessageLimit: json.limits.sessionMessageLimit ?? 50,
          tokenLimitPerDevice: json.limits.tokenLimitPerDevice ?? 100000,
          timeWindowHours: json.limits.timeWindowHours ?? 24,
          freeTierTokensPerUser: json.limits.freeTierTokensPerUser ?? 500000,
          paidTierTokensPerUser: json.limits.paidTierTokensPerUser ?? null,
          dailyTokensPerUser: json.limits.dailyTokensPerUser ?? null,
        });
      }
      if (json.isPlatformAdmin) {
        try {
          const aiTiersRes = await api.get<{ tiers: AiTier[] }>('/ai/admin/tiers');
          setAiTiers(aiTiersRes.data?.tiers ?? []);
        } catch {
          setAiTiers([]);
        }
        try {
          const usersRes = await api.get<{ users: UserWithTier[] }>('/ai/admin/users-with-tiers');
          setUsersWithTiers(usersRes.data?.users ?? []);
        } catch {
          setUsersWithTiers([]);
        }
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } };
      const msg = err.response?.status === 403 ? 'Admin access required' : (e instanceof Error ? e.message : 'Failed to load');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (showTierModal) {
      if (editingTier) {
        setTierForm({
          name: editingTier.name,
          dailyTokenCap: editingTier.dailyTokenCap,
          defaultModelConfigurationId: editingTier.defaultModelConfigurationId ?? null,
          modelIds: editingTier.modelIds ?? [],
        });
      } else {
        setTierForm({
          name: '',
          dailyTokenCap: 500000,
          defaultModelConfigurationId: null,
          modelIds: [],
        });
      }
    }
  }, [showTierModal, editingTier]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/ai/admin/limits', {
        sessionMessageLimit: form.sessionMessageLimit,
        timeWindowHours: form.timeWindowHours,
      });
      await load(true);
      toast.success('Free-usage limits saved.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string }; status?: number } };
      const msg = err.response?.status === 403
        ? 'Admin access required'
        : (err.response?.data?.message || err.response?.data?.error || (e instanceof Error ? e.message : 'Failed to save'));
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const openTierEdit = (tier: AiTier) => {
    setEditingTier(tier);
    setShowTierModal(true);
  };

  const handleDeleteTier = async (tierId: string) => {
    if (!window.confirm('Delete this tier? Users on it will fall back to Authorized free.')) return;
    try {
      await api.delete(`/ai/admin/tiers/${tierId}`);
      const aiTiersRes = await api.get<{ tiers: AiTier[] }>('/ai/admin/tiers');
      setAiTiers(aiTiersRes.data?.tiers ?? []);
      toast.success('Tier deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete tier');
    }
  };

  const handleSaveTier = async () => {
    const name = tierForm.name.trim();
    if (!name) {
      toast.error('Tier name is required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editingTier) {
        await api.put(`/ai/admin/tiers/${editingTier.id}`, {
          name: tierForm.name.trim(),
          dailyTokenCap: tierForm.dailyTokenCap,
          defaultModelConfigurationId: tierForm.defaultModelConfigurationId || null,
        });
        await api.put(`/ai/admin/tiers/${editingTier.id}/models`, { modelIds: tierForm.modelIds });
      } else {
        const createRes = await api.post<AiTier>('/ai/admin/tiers', {
          name: tierForm.name.trim(),
          dailyTokenCap: tierForm.dailyTokenCap,
          defaultModelConfigurationId: tierForm.defaultModelConfigurationId || null,
        });
        const newId = createRes.data.id;
        await api.put(`/ai/admin/tiers/${newId}/models`, { modelIds: tierForm.modelIds });
      }
      const aiTiersRes = await api.get<{ tiers: AiTier[] }>('/ai/admin/tiers');
      setAiTiers(aiTiersRes.data?.tiers ?? []);
      setShowTierModal(false);
      setEditingTier(null);
      toast.success(editingTier ? 'Tier updated.' : 'Tier created.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const msg = err.response?.data?.message || (e instanceof Error ? e.message : 'Failed to save tier');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddModel = async () => {
    const modelName = addForm.modelName.trim();
    const displayName = addForm.displayName.trim();
    if (!modelName || !displayName) {
      toast.error('Provider and display name are required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const configuration: Record<string, string> = {};
      if (addForm.endpoint.trim()) configuration.endpoint = addForm.endpoint.trim();
      if (addForm.model.trim()) configuration.model = addForm.model.trim();
      if (addForm.apiKey.trim()) configuration.api_key = addForm.apiKey;
      await api.post('/ai/admin/platform-models', {
        modelName,
        displayName,
        description: addForm.description.trim() || undefined,
        configuration,
        setAsDefault: false,
      });
      await load(true);
      setShowAddModal(false);
      setAddForm({ modelName: '', displayName: '', description: '', endpoint: '', model: '', apiKey: '' });
      toast.success('Model added.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const msg = err.response?.data?.message || (e instanceof Error ? e.message : 'Failed to add model');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfigureModel = (model: PlatformModel) => {
    setConfiguringModel(model);
    const cfg = model.configuration || {};
    const apiKey = (cfg['api_key'] ?? cfg['apiKey'] ?? '') as string;
    const modelVal = (cfg['model'] ?? cfg['ollamaModel'] ?? '') as string;
    setConfigForm({
      modelName: model.modelName || '',
      displayName: model.displayName || '',
      description: model.description || '',
      apiKey: apiKey || '',
      endpoint: (cfg['endpoint'] as string) || '',
      currentModel: modelVal,
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!configuringModel) return;
    const modelName = configForm.modelName.trim();
    const displayName = configForm.displayName.trim();
    if (!modelName || !displayName) {
      toast.error('Provider and display name are required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const configuration: Record<string, string> = {
        endpoint: configForm.endpoint?.trim() || '',
        model: configForm.currentModel?.trim() || '',
      };
      if (configForm.apiKey && configForm.apiKey.length >= 10 && !/^[\w-]+\.\.\.$/.test(configForm.apiKey)) {
        configuration.api_key = configForm.apiKey;
      }
      await api.put('/ai/admin/platform-models', {
        id: configuringModel.id,
        modelName,
        displayName,
        description: configForm.description?.trim() || undefined,
        configuration,
      });
      await load();
      setShowConfigModal(false);
      setConfiguringModel(null);
      toast.success('Model configuration updated.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || (e instanceof Error ? e.message : 'Failed to update'));
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004C94]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-[#004C94] hover:underline font-medium"
            >
              ← Admin
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#004D99]">
                AI free usage settings
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Configure limits for free-tier planning and confirm which LLM serves it.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 text-red-800 text-sm">
            {error}
          </div>
        )}

        {data?.isPlatformAdmin && (
          <>
            <ResearchSearchSettingsCard mode="platform" />

            {/* Platform models: add and configure */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Platform models
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Add and configure LLM providers used for free-tier and paid users when no tenant default is set.
              </p>
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setAddForm({ modelName: '', displayName: '', description: '', endpoint: '', model: '', apiKey: '' });
                    setShowAddModal(true);
                  }}
                  className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90"
                >
                  Add model
                </button>
              </div>
              {(data.platformModels?.length ?? 0) > 0 ? (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(data.platformModels ?? []).map((m) => (
                        <tr key={m.id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900">{m.displayName}</span>
                            <span className="text-sm text-gray-500 ml-1">({m.modelName})</span>
                          </td>
                          <td className="px-4 py-3">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!m.isActive}
                                onChange={async () => {
                                  try {
                                    setSaving(true);
                                    await api.put('/ai/admin/platform-models', { id: m.id, isActive: !m.isActive });
                                    await load(true);
                                    toast.success(m.isActive ? 'Model set inactive.' : 'Model set active.');
                                  } catch (e: unknown) {
                                    const err = e as { response?: { data?: { message?: string } } };
                                    toast.error(err.response?.data?.message ?? 'Failed to update');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                                disabled={saving}
                                className="rounded border-gray-300 text-[#004C94] focus:ring-[#004C94]"
                              />
                              <span className="text-sm text-gray-700">{m.isActive ? 'Active' : 'Inactive'}</span>
                            </label>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => handleConfigureModel(m)}
                              className="text-[#004C94] hover:underline text-sm mr-3"
                            >
                              Configure
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${m.displayName}"? This cannot be undone.`)) return;
                                try {
                                  setSaving(true);
                                  await api.delete(`/ai/admin/platform-models/${m.id}`);
                                  await load();
                                  toast.success('Model deleted.');
                                } catch (e: unknown) {
                                  const err = e as { response?: { data?: { message?: string } } };
                                  toast.error(err.response?.data?.message ?? 'Failed to delete');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              disabled={saving}
                              className="text-red-600 hover:underline text-sm"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No platform models yet. Add one to set defaults below.</p>
              )}
            </div>

            {/* AI Tiers – right under Platform models */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                AI Tiers
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Each user belongs to one tier. Built-in tiers: <strong>Unauthorized free</strong> (anonymous), <strong>Authorized free</strong> (logged-in default). Add custom tiers and assign models and caps.
              </p>
              {aiTiers.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Daily token cap</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Default model</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {aiTiers.map((t) => (
                        <tr key={t.id}>
                          <td className="px-4 py-2 text-sm text-gray-900">{t.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{t.dailyTokenCap.toLocaleString()}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {t.defaultModelConfigurationId
                              ? (data.platformModels ?? []).find((m) => m.id === t.defaultModelConfigurationId)?.displayName ?? '—'
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-sm">{t.isSystem ? <span className="text-gray-500">System</span> : 'Custom'}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => openTierEdit(t)}
                              className="text-[#004C94] hover:underline text-sm mr-2"
                            >
                              Edit
                            </button>
                            {!t.isSystem && (
                              <button
                                type="button"
                                onClick={() => handleDeleteTier(t.id)}
                                className="text-red-600 hover:underline text-sm"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No tiers. Run the database migration to seed built-in tiers.</p>
              )}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => { setEditingTier(null); setShowTierModal(true); }}
                  className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90"
                >
                  Add tier
                </button>
              </div>
            </div>

            {/* User tier assignment */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                User tier assignment
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Assign a tier to each user. Users without an assigned tier use <strong>Authorized free</strong>.
              </p>
              {usersWithTiers.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">User</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Tier</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usersWithTiers.map((u) => (
                        <tr key={u.userId}>
                          <td className="px-4 py-2 text-sm text-gray-900">{u.email}</td>
                          <td className="px-4 py-2">
                            <select
                              value={u.tierId ?? ''}
                              onChange={async (e) => {
                                const newTierId = e.target.value || null;
                                try {
                                  await api.put(`/ai/admin/users/${u.userId}/tier`, { tierId: newTierId });
                                  setUsersWithTiers((prev) => prev.map((x) => (x.userId === u.userId ? { ...x, tierId: newTierId, tierName: newTierId ? aiTiers.find((t) => t.id === newTierId)?.name ?? null : null } : x)));
                                  toast.success('Tier updated.');
                                } catch (err) {
                                  toast.error(err instanceof Error ? err.message : 'Failed to update tier');
                                }
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Authorized free (default)</option>
                              {aiTiers.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No users found.</p>
              )}
            </div>
          </>
        )}

        {/* Free usage limits */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Free usage limits
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Session and time-window limits. Daily token caps are defined per <strong>AI tier</strong> (Platform admins: configure in AI Tiers above).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session message limit
              </label>
              <input
                type="number"
                min={0}
                value={form.sessionMessageLimit}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    sessionMessageLimit: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#004C94]"
              />
              <p className="text-xs text-gray-500 mt-1">Max messages per planning session (optional cap).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time window (hours)
              </label>
              <input
                type="number"
                min={1}
                value={form.timeWindowHours}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    timeWindowHours: parseInt(e.target.value, 10) || 24,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#004C94]"
              />
              <p className="text-xs text-gray-500 mt-1">Rolling window for daily caps (e.g. 24 = daily).</p>
            </div>
          </div>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save limits'}
            </button>
          </div>
        </div>

        {/* Add model modal (Platform Admin) */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add platform model</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider (e.g. openai, claude, ollama)</label>
                  <input
                    type="text"
                    value={addForm.modelName}
                    onChange={(e) => setAddForm((f) => ({ ...f, modelName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="openai"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                  <input
                    type="text"
                    value={addForm.displayName}
                    onChange={(e) => setAddForm((f) => ({ ...f, displayName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="OpenAI GPT-4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={addForm.description}
                    onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint (optional)</label>
                  <input
                    type="text"
                    value={addForm.endpoint}
                    onChange={(e) => setAddForm((f) => ({ ...f, endpoint: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="https://api.openai.com/v1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model (optional)</label>
                  <input
                    type="text"
                    value={addForm.model}
                    onChange={(e) => setAddForm((f) => ({ ...f, model: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="gpt-4o-mini"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API key (optional, can set later)</label>
                  <input
                    type="password"
                    value={addForm.apiKey}
                    onChange={(e) => setAddForm((f) => ({ ...f, apiKey: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleAddModel} disabled={saving} className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50">Add</button>
              </div>
            </div>
          </div>
        )}

        {/* Configure model modal (Platform Admin) */}
        {showConfigModal && configuringModel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit platform model</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider (e.g. openai, claude, ollama)</label>
                  <input
                    type="text"
                    value={configForm.modelName}
                    onChange={(e) => setConfigForm((f) => ({ ...f, modelName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="openai"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                  <input
                    type="text"
                    value={configForm.displayName}
                    onChange={(e) => setConfigForm((f) => ({ ...f, displayName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="OpenAI GPT-4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={configForm.description}
                    onChange={(e) => setConfigForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint</label>
                  <input
                    type="text"
                    value={configForm.endpoint}
                    onChange={(e) => setConfigForm((f) => ({ ...f, endpoint: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={configForm.currentModel}
                    onChange={(e) => setConfigForm((f) => ({ ...f, currentModel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API key</label>
                  <input
                    type="password"
                    value={configForm.apiKey}
                    onChange={(e) => setConfigForm((f) => ({ ...f, apiKey: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Leave blank to keep existing"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => { setShowConfigModal(false); setConfiguringModel(null); }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleSaveConfig} disabled={saving} className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit tier modal (Platform Admin) */}
        {showTierModal && data?.isPlatformAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{editingTier ? 'Edit tier' : 'Create tier'}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={tierForm.name}
                    onChange={(e) => setTierForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g. Power user"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daily token cap</label>
                  <input
                    type="number"
                    min={0}
                    value={tierForm.dailyTokenCap}
                    onChange={(e) => setTierForm((f) => ({ ...f, dailyTokenCap: parseInt(e.target.value, 10) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default model</label>
                  <select
                    value={tierForm.defaultModelConfigurationId ?? ''}
                    onChange={(e) => setTierForm((f) => ({ ...f, defaultModelConfigurationId: e.target.value || null }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">— None —</option>
                    {(data.platformModels ?? []).filter((m) => m.isActive).map((m) => (
                      <option key={m.id} value={m.id}>{m.displayName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Models in this tier</label>
                  <p className="text-xs text-gray-500 mb-2">Users in this tier can use only these models.</p>
                  <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                    {(data.platformModels ?? []).filter((m) => m.isActive).map((m) => (
                      <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tierForm.modelIds.includes(m.id)}
                          onChange={(e) => {
                            setTierForm((f) => ({
                              ...f,
                              modelIds: e.target.checked ? [...f.modelIds, m.id] : f.modelIds.filter((id) => id !== m.id),
                            }));
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-900">{m.displayName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowTierModal(false); setEditingTier(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTier}
                  disabled={saving}
                  className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004C94]/90 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingTier ? 'Save' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAIFreeUsagePage;
